/**
 * ============================================================================
 * ForgotPasswordPage.js - Password Reset Request Page
 * ============================================================================
 * Allows users to request a password reset email
 * Created: November 27, 2025 - Phase 2C Multi-Tenancy
 * 
 * FEATURES:
 * - Email input for password reset
 * - Success message after submission
 * - Rate limiting awareness
 * - Link back to login
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function ForgotPasswordPage() {
  const { forgotPassword, logout } = useAuth();

  // Clear any existing auth state so stale sessions don't interfere
  useEffect(() => {
    logout();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Form state
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate email
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await forgotPassword(email);
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setError(result.error || 'Request failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Success state - show confirmation message
  if (isSubmitted) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 40,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          textAlign: 'center'
        }}>
          {/* Logo */}
          <div style={{
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 4px 12px rgba(45, 106, 79, 0.1)',
            padding: 8
          }}>
            <img 
              src="/logo.png" 
              alt="Tee Time Organizer" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
            />
          </div>
          
          <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 700, color: '#1b4332' }}>
            Check Your Email
          </h1>
          
          <p style={{ 
            margin: '0 0 8px', 
            fontSize: 15, 
            color: '#4b5563',
            lineHeight: 1.5
          }}>
            If an account exists for <strong>{email}</strong>, we've sent password reset instructions.
          </p>
          
          <p style={{ 
            margin: '0 0 24px', 
            fontSize: 13, 
            color: '#6b7280',
            lineHeight: 1.5
          }}>
            Didn't receive the email? Check your spam folder or try again in a few minutes.
          </p>
          
          {/* Back to Login */}
          <Link 
            to="/login"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: '#2d6a4f',
              color: '#fff',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 15,
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#1b4332'}
            onMouseLeave={(e) => e.target.style.background = '#2d6a4f'}
          >
            Return to Login
          </Link>
          
          {/* Try Again Link */}
          <div style={{ marginTop: 16 }}>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setEmail('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#2d6a4f',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Try a different email
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Request form
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 40,
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Logo and Title */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 72,
            height: 72,
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px',
            boxShadow: '0 4px 12px rgba(45, 106, 79, 0.1)',
            padding: 6
          }}>
            <img 
              src="/logo.png" 
              alt="Tee Time Organizer" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
            />
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1b4332' }}>
            Forgot Password?
          </h1>
          <p style={{ margin: '10px 0 0', fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>
            No worries! Enter your email and we'll send you reset instructions.
          </p>
        </div>
        
        {/* Request Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Email Field */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: 8, 
              fontSize: 14, 
              fontWeight: 600, 
              color: '#374151' 
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder="you@example.com"
              disabled={isSubmitting}
              autoComplete="email"
              autoFocus
              style={{
                width: '100%',
                padding: '12px 16px',
                border: error ? '2px solid #ef4444' : '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isSubmitting ? '#f9fafb' : '#fff',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                if (!error) e.target.style.borderColor = '#2d6a4f';
              }}
              onBlur={(e) => {
                if (!error) e.target.style.borderColor = '#e5e7eb';
              }}
            />
          </div>
          
          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#b91c1c',
              fontSize: 14
            }}>
              {error}
            </div>
          )}
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '14px',
              background: isSubmitting ? '#9ca3af' : '#2d6a4f',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: isSubmitting ? 'none' : '0 2px 8px rgba(45, 106, 79, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.target.style.background = '#1b4332';
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.target.style.background = '#2d6a4f';
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        
        {/* Back to Login Link */}
        <div style={{
          marginTop: 24,
          textAlign: 'center'
        }}>
          <Link 
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              color: '#2d6a4f',
              textDecoration: 'none',
              fontWeight: 500
            }}
          >
            <span style={{ fontSize: 18 }}>←</span>
            Back to Login
          </Link>
        </div>
        
        {/* Help Text */}
        <div style={{
          marginTop: 20,
          padding: '14px',
          background: '#f9fafb',
          borderRadius: 8,
          fontSize: 12,
          color: '#6b7280',
          textAlign: 'center',
          lineHeight: 1.5
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Need Help?</div>
          <div>Contact your organization administrator if you're having trouble accessing your account.</div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
